import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Chip, Tooltip } from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  Memory as MemoryIcon,
  Schedule as ScheduleIcon
} from "@material-ui/icons";
import {
  ATA_STATUS,
  getAtaStatusLabel,
  resolveAtaStatus
} from "./ataDraftStore";
import { theme as appTheme } from "../../assets/theme/StyledTheme";

function statusTooltipKey(resolvedStatus) {
  if (resolvedStatus === ATA_STATUS.APROVADA) return "smartMinutes.status.approvedHint";
  if (resolvedStatus === ATA_STATUS.PROCESSADA_IA) {
    return "smartMinutes.status.aiProcessedHint";
  }
  if (resolvedStatus === ATA_STATUS.VOZ_PROCESSADA) {
    return "smartMinutes.status.voiceProcessedHint";
  }
  return "smartMinutes.status.draftHint";
}

/**
 * Status principal da ata + label auxiliar de IA quando já processada.
 * Aprovada = destaque; “Processada pela IA” fica secundária.
 */
export default function AtaStatusChip({ status, processedByAi, ata }) {
  const { t } = useTranslation();
  const resolvedStatus = resolveAtaStatus(ata || { status, processedByAi });
  const ai =
    processedByAi === true ||
    (ata && ata.processedByAi === true) ||
    resolvedStatus === ATA_STATUS.PROCESSADA_IA ||
    resolvedStatus === ATA_STATUS.VOZ_PROCESSADA;

  const label = getAtaStatusLabel(resolvedStatus) || resolvedStatus;
  const statusHint = t(statusTooltipKey(resolvedStatus));

  let primary = (
    <Chip
      size="small"
      icon={<ScheduleIcon />}
      label={label}
      style={{ background: "#eceff1", color: "#455a64" }}
    />
  );

  if (resolvedStatus === ATA_STATUS.APROVADA) {
    primary = (
      <Chip
        size="small"
        icon={<CheckCircleIcon />}
        label={label}
        style={{
          background: appTheme.color.primary.light,
          color: appTheme.color.primary.dark
        }}
      />
    );
  } else if (resolvedStatus === ATA_STATUS.PROCESSADA_IA) {
    // Em lista/filtro ainda sem aprovação: IA como status principal
    primary = (
      <Chip
        size="small"
        icon={<MemoryIcon />}
        label={label}
        style={{ background: "#fff3e0", color: "#e65100" }}
      />
    );
  } else if (resolvedStatus === ATA_STATUS.VOZ_PROCESSADA) {
    primary = (
      <Chip
        size="small"
        icon={<MemoryIcon />}
        label={label}
        style={{ background: "#e3f2fd", color: "#1565c0" }}
      />
    );
  }

  const showAiAux = ai && resolvedStatus === ATA_STATUS.APROVADA;

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      style={{ gap: 6 }}
      flexWrap="wrap"
    >
      <Tooltip title={statusHint} enterDelay={400}>
        <span>{primary}</span>
      </Tooltip>
      {showAiAux ? (
        <Tooltip
          title={t("smartMinutes.status.aiProcessedTagHint")}
          enterDelay={400}
        >
          <span>
            <Chip
              size="small"
              variant="outlined"
              icon={<MemoryIcon style={{ fontSize: 16 }} />}
              label={t("smartMinutes.status.aiProcessed")}
              style={{
                borderColor: "#ffcc80",
                color: "#e65100",
                background: "transparent",
                height: 24
              }}
            />
          </span>
        </Tooltip>
      ) : null}
    </Box>
  );
}

AtaStatusChip.propTypes = {
  status: PropTypes.string,
  processedByAi: PropTypes.bool,
  ata: PropTypes.shape({
    status: PropTypes.string,
    processedByAi: PropTypes.bool
  })
};

AtaStatusChip.defaultProps = {
  status: "",
  processedByAi: false,
  ata: null
};
